'use client';

import { Home, Phone, HelpCircle } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';

import { useProductStore } from '@/presentation/hooks/useStores';
import { Separator } from '@/components/ui/separator';
import RequestCallbackModal from './RequestCallbackModal';

interface MobileNavigationProps {
  onClose: () => void;
}

/**
 * Mobile navigation items for the side drawer.
 * Focuses on browsing (Home, Categories) and Support.
 * Account actions (Orders, Rep Score, Profile) are in the header dropdown.
 */
const MobileNavigation = observer(({ onClose }: MobileNavigationProps) => {
  const router = useRouter();
  const productStore = useProductStore();
  const categories = productStore.categoryNavigation;
  const [isCallbackModalOpen, setCallbackModalOpen] = useState(false);

  const handleNavClick = (path: string) => {
    router.push(path);
    onClose();
  };

  const openCallbackModal = () => {
    setCallbackModalOpen(true);
  };

  const handleCallbackModalClose = (isOpen: boolean) => {
    setCallbackModalOpen(isOpen);
    if (!isOpen) {
      onClose();
    }
  };

  const openHelpCenter = () => {
    window.open('https://support.motito.co', '_blank');
    onClose();
  };

  return (
    <>
      {/* Request Callback Modal */}
      <RequestCallbackModal open={isCallbackModalOpen} onOpenChange={handleCallbackModalClose} />

      <nav className="flex flex-col gap-4 py-4">
        {/* Main Navigation */}
        <div className="flex flex-col gap-1">
          <button
            onClick={() => handleNavClick('/')}
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-gray-100"
          >
            <Home className="size-5 text-gray-600" />
            <span className="font-medium">Home</span>
          </button>
        </div>

        <Separator />

        {/* Browse Categories */}
        <div className="flex flex-col gap-1">
          <p className="px-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Browse Categories
          </p>
          {productStore.isLoading ? (
            <p className="px-3 py-2 text-sm text-gray-500">Loading categories...</p>
          ) : (
            categories.map((category) => (
              <Link
                key={category.id}
                href={`/category/${category.slug}`}
                onClick={onClose}
                className="rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-gray-100"
              >
                <span>{category.name}</span>
              </Link>
            ))
          )}
        </div>

        <Separator />

        {/* Support */}
        <div className="flex flex-col gap-1">
          <p className="px-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Support
          </p>

          <button
            onClick={openCallbackModal}
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-gray-100"
          >
            <Phone className="size-5 text-gray-600" />
            <span>Request a call back</span>
          </button>

          <button
            onClick={openHelpCenter}
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-gray-100"
          >
            <HelpCircle className="size-5 text-gray-600" />
            <span>Help center</span>
          </button>
        </div>
      </nav>
    </>
  );
});

MobileNavigation.displayName = 'MobileNavigation';

export default MobileNavigation;
